/**
 * Воспроизводимая случайность.
 *
 * Подбору нужна случайность, но не бесконтрольная: одинаковая погода не должна
 * давать один и тот же набор, а тест обязан получать один и тот же результат.
 * Поэтому источник — генератор с явным seed, а не Math.random. Сессия берёт
 * свой seed из места и часа, тест — из константы.
 *
 * Алгоритм — mulberry32: тридцать строк, период 2^32, распределение ровное
 * настолько, насколько это здесь нужно. Криптостойкость не требуется.
 */
export type Rng = () => number;

export function makeRng(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Строка → seed. FNV-1a: коротко и разводит близкие строки. */
export function seedFrom(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Выбор индекса по softmax от значений: лучшее побеждает чаще, но не всегда.
 *
 * Температура правит остротой. При нуле это обычный максимум — им пользуются
 * тесты, которым нужен предсказуемый ответ, и он же остаётся поведением по
 * умолчанию там, где случайность не заказана явно.
 */
export function softmaxPick(values: number[], rng: Rng, temperature: number): number {
  if (!values.length) return -1;
  let bestIdx = 0;
  for (let i = 1; i < values.length; i++) if (values[i]! > values[bestIdx]!) bestIdx = i;
  if (temperature <= 0) return bestIdx;

  // Вычитаем максимум перед экспонентой: без этого exp() переполняется на
  // больших значениях и весь выбор схлопывается в NaN.
  const best = values[bestIdx]!;
  const weights = values.map((v) => Math.exp((v - best) / temperature));
  const total = weights.reduce((a, b) => a + b, 0);
  if (!Number.isFinite(total) || total <= 0) return bestIdx;

  let r = rng() * total;
  for (let i = 0; i < weights.length; i++) {
    r -= weights[i]!;
    if (r <= 0) return i;
  }
  return bestIdx;
}
